<template>
    <DashboardContentLayout class="space-y-6" :heading="$t('modules.hitlist.title')" :has-period-picker="false" :has-pig-type-picker="true">
        <template #header-top-right>
            <div class="relative">
                <Listbox v-slot="{ open }" v-model="selectedQuarterKey">
                    <ListboxButton class="min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left">
                        <div class="relative">
                            <!-- Invisible sizer: determines button width from the longest available option -->
                            <span class="invisible whitespace-nowrap font-medium" aria-hidden="true">{{ longestOptionLabel }}</span>
                            <!-- Visible selected label, overlaid on the sizer -->
                            <span class="absolute inset-0 flex items-center font-medium whitespace-nowrap">{{ selectedQuarterLabel }}</span>
                        </div>
                        <ChevronDown class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': open }" aria-hidden="true" />
                    </ListboxButton>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-out"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <ListboxOptions class="absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <ListboxOption v-for="option in quarterOptions" :key="option.key" v-slot="{ active, selected }" :value="option.key">
                                <li
                                    class="py-2 px-3 h-full truncate block w-full text-left cursor-pointer"
                                    :class="{
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }"
                                >
                                    {{ option.label }}
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </Listbox>
            </div>
        </template>

        <div class="space-y-4">
            <SwitchButton
                v-model="selectedScope"
                :true-text="$t('hitlist.scope.farms')"
                :false-text="$t('hitlist.scope.enterprise')"
                true-value="farms"
                false-value="directory"
            />
            <div v-if="isError" class="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700">
                <p class="text-base font-medium">{{ $t('hitlist.loadError') }}</p>
                <Button variant="secondary" size="sm" @click="refetch">{{ $t('hitlist.retry') }}</Button>
            </div>
            <div v-else class="hitlist-tabs default-tabs">
                <Tabs teleport="#hitlist-tabs-target" active-tab-class="--active" @rewrite="onTabChange">
                    <nav class="space-x-8 flex border-b border-black border-opacity-10">
                        <div class="flex pr-4">
                            <Tab :name="$t('hitlist.tabs.graph')" slug="graph" :default="true">
                                <GenericDashboard
                                    v-if="graphDashboard || isFetching"
                                    :data="graphDashboard"
                                    :loading="isFetching"
                                    :fetch-params="fetchParams"
                                    :fetch-action-type="ActionType.GetHitlistDashboard"
                                    :focused-column="tbFocusBy"
                                    :no-data-text="$t('hitlist.noData')"
                                    @update:item="onUpdateItem"
                                    @update:section-collapse="onUpdateSectionCollapse"
                                    @focus-column="onFocusColumn"
                                />
                                <div v-else class="py-14 text-center px-4 text-gray-500 text-lg">{{ $t('hitlist.noData') }}</div>
                            </Tab>
                        </div>
                        <div class="flex pr-4">
                            <Tab :name="$t('hitlist.tabs.table')" slug="table">
                                <GenericDashboard
                                    v-if="tableDashboard || isFetching"
                                    :data="tableDashboard"
                                    :loading="isFetching"
                                    :fetch-params="fetchParams"
                                    :fetch-action-type="ActionType.GetHitlistDashboard"
                                    :focused-column="tbFocusBy"
                                    :no-data-text="$t('hitlist.noData')"
                                    @update:item="onUpdateItem"
                                    @update:section-collapse="onUpdateSectionCollapse"
                                    @focus-column="onFocusColumn"
                                />
                                <div v-else class="py-14 text-center px-4 text-gray-500 text-lg">{{ $t('hitlist.noData') }}</div>
                            </Tab>
                        </div>
                    </nav>
                </Tabs>
                <div id="hitlist-tabs-target" class="mt-6" />
            </div>
        </div>
    </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { ChevronDown } from 'lucide-vue-next';
import { cloneDeep } from 'lodash-es';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import SwitchButton from '@/components/ui/SwitchButton.vue';
import Button from '@/components/ui/button/Button.vue';
import GenericDashboard from '@/components/common/dashboard/GenericDashboard.vue';
import { useHitlistDashboard } from '@/plugins/store/actions/queries/hitlist/dashboards';
import { buildQuarterKey, buildDynamicQuarterKey, generateQuarterOptions, type DynamicQuarterOption } from '@/util/quarters';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { Dashboard, DashboardItem, DashboardSectionWithItems, TableHeader } from '@/types';
import { i18n } from '@/plugins/internationalization/i18n';

type HitlistScope = 'farms' | 'directory';
type HitlistTab = 'graph' | 'table';

const quarterOptions = computed<DynamicQuarterOption[]>(() =>
    generateQuarterOptions(new Date(), 4, true, i18n.global.t('hitlist.dropdown.dynamicEstimates') as string)
);
const defaultQuarterKey = computed(() => {
    // First option is always dynamic estimates
    return quarterOptions.value[0]?.key || buildDynamicQuarterKey(new Date().getFullYear(), 1);
});

const selectedScope = ref<HitlistScope>('farms');
const selectedQuarterKey = ref<string>(defaultQuarterKey.value);
const selectedTab = ref<HitlistTab>('graph');
const tbFocusBy = ref<string | null>(null);
const focusInitializedForRender = ref<string | null>(null);

const isWeaners = computed(() => store.getters.getIsWeaners);
const locationIds = computed(() => store.state.selectedFarmIds);

const scope = computed(() => selectedScope.value);
const quarterKey = computed(() => selectedQuarterKey.value);

const render = computed(() => selectedTab.value);

const { data, isFetching, isError, refetch } = useHitlistDashboard({
    scope,
    t: quarterKey,
    isWeaners,
    render,
    locationIds,
    tbFocusBy,
});

const dashboard = ref<Dashboard | undefined>();

function detectDefaultFocusColumn(dash: Dashboard): string | null {
    for (const item of dash.items) {
        if (item.type === 'table' && item.table?.interaction?.mode === 'focus') {
            const header = (item.table.headers as TableHeader[])?.find((h) => h.focusable);
            if (header) return header.value;
        }
    }
    return null;
}

watch(
    [data, isFetching],
    ([newData, fetching]) => {
        dashboard.value = newData ? cloneDeep(newData) : undefined;

        if (!fetching && newData && !tbFocusBy.value && focusInitializedForRender.value !== render.value) {
            const col = detectDefaultFocusColumn(newData);
            if (col) {
                tbFocusBy.value = col;
            }
            focusInitializedForRender.value = render.value;
        }
    },
    { immediate: true }
);

const selectedQuarterLabel = computed(() => quarterOptions.value.find((option) => option.key === selectedQuarterKey.value)?.label || '');

const longestOptionLabel = computed(() =>
    quarterOptions.value.reduce((longest, option) => (option.label.length > longest.length ? option.label : longest), '')
);

const fetchParams = computed(() => ({
    scope: selectedScope.value,
    t: selectedQuarterKey.value,
    isWeaners: isWeaners.value,
    render: selectedTab.value,
    locationIds: locationIds.value,
    tbFocusBy: tbFocusBy.value,
}));

const graphDashboard = computed(() => dashboard.value);
const tableDashboard = computed(() => dashboard.value);

function onUpdateItem({ item }: { item: DashboardItem }) {
    const itemIndex = dashboard.value!.items.findIndex((i) => i.item_no === item.item_no);

    if (itemIndex !== -1) {
        dashboard.value!.items[itemIndex] = item;
    }
}

function onUpdateSectionCollapse({ sectionIndex, isCollapsed }: { sectionIndex: number; isCollapsed: boolean }) {
    if (dashboard.value?.sections?.[sectionIndex]) {
        (dashboard.value.sections[sectionIndex] as DashboardSectionWithItems).is_collapsed = isCollapsed;
    }
}

function onFocusColumn({ column }: { itemNo: number; column: string }) {
    if (tbFocusBy.value !== column) {
        tbFocusBy.value = column;
    }
}

function onTabChange(url: string) {
    const tabId = url.replace('#', '');

    if (tabId === 'graph' || tabId === 'table') {
        selectedTab.value = tabId;
    } else {
        selectedTab.value = 'graph';
    }
}

watch(selectedTab, () => {
    tbFocusBy.value = null;
    focusInitializedForRender.value = null;
});
</script>
